/**
 * Carga masiva por Excel (bloque 13): se descarga la plantilla, se llena y se sube. El sistema primero REVISA el
 * archivo y muestra qué va a crear, qué se queda igual y qué filas tienen errores; solo al confirmar se carga.
 * Sirve para productos, clientes y la existencia inicial de un almacén.
 */
import type { AccionImportacion, ImportacionDTO } from '@mg/shared';
import { useMutation } from '@tanstack/react-query';
import { Download, FileSpreadsheet } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Select } from '@/componentes/ui/input.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/componentes/ui/table.tsx';
import { getToken, mensajeDeError, post } from '@/lib/api.ts';

type Que = 'productos' | 'clientes' | 'existencias';
const VARIANTE: Record<AccionImportacion, 'ok' | 'pendiente' | 'neutro' | 'critico'> = { crear: 'ok', actualizar: 'pendiente', omitir: 'neutro', error: 'critico' };
/** Qué pasa con la fila: antes de cargar y después de cargada. */
const ACCION: Record<AccionImportacion, [antes: string, despues: string]> = { crear: ['Se crea', 'Creado'], actualizar: ['Se actualiza', 'Actualizado'], omitir: ['Se deja igual', 'Quedó igual'], error: ['Error', 'No entró'] };
const TEXTOS: Record<Que, { titulo: string; descripcion: string; clave: string; cosas: string; crean: [string, string]; entra: [string, string] }> = {
  productos: { titulo: 'Cargar productos desde Excel', descripcion: 'Descarga la plantilla, llénala y súbela. Antes de cargar verás qué se va a crear y qué filas tienen errores.', clave: 'Código', cosas: 'productos', crean: ['se crean', 'creados'], entra: ACCION.crear },
  clientes: { titulo: 'Cargar clientes desde Excel', descripcion: 'Descarga la plantilla, llénala y súbela. Antes de cargar verás qué se va a crear y qué filas tienen errores.', clave: 'RIF', cosas: 'clientes', crean: ['se crean', 'creados'], entra: ACCION.crear },
  // Después de cargar no se dice «entraron»: si el ajuste quedó por aprobar, todavía no entró nada; la nota dice cuál de
  // los dos pasó (re-auditoría 2026-10-18, ronda 2, hallazgo 3-H13)
  existencias: { titulo: 'Cargar existencia inicial desde Excel', descripcion: 'Elige el almacén y descarga la plantilla: trae los productos que nunca han tenido existencia allí (los que ya tuvieron y hoy están en cero entran por una compra, un conteo o un ajuste). Escribe la cantidad de cada uno y súbela. Entra como un ajuste «Inventario inicial».', clave: 'Código', cosas: 'productos', crean: ['entran', 'van en el ajuste'], entra: ['Entra', 'En el ajuste'] },
};
const MAX_BYTES = 2 * 1024 * 1024;

/** Archivo → base64 (sin el prefijo «data:…»). */
function aBase64(archivo: File): Promise<string> {
  return new Promise((resolver, rechazar) => {
    const lector = new FileReader();
    lector.addEventListener('load', () => resolver(String(lector.result).split(',')[1] ?? ''));
    lector.addEventListener('error', () => rechazar(new Error('No se pudo leer el archivo')));
    lector.readAsDataURL(archivo);
  });
}

async function descargar(ruta: string, nombre: string): Promise<void> {
  const res = await fetch(`/api${ruta}`, { headers: { authorization: `Bearer ${getToken() ?? ''}` } });
  if (!res.ok) throw new Error(`No se pudo descargar la plantilla (${res.status})`);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

type Archivo = { nombre: string; base64: string };

export function ImportarExcel({ que, abierto, cerrar, alCargar, almacenes = [], tema }: { que: Que; abierto: boolean; cerrar: () => void; alCargar: () => void; /** Solo para la existencia inicial: dónde entra. */ almacenes?: { id: string; nombre: string }[]; /** Tema de ayuda de la ventana (el «?» junto al título). */ tema?: string }) {
  const t = TEXTOS[que];
  const porAlmacen = que === 'existencias';
  const entrada = useRef<HTMLInputElement>(null);
  const [archivo, setArchivo] = useState<Archivo | null>(null);
  const [actualizar, setActualizar] = useState(false);
  const [almacenId, setAlmacenId] = useState('');
  const [revision, setRevision] = useState<ImportacionDTO | null>(null);
  const enviar = (simular: boolean, a = archivo, act = actualizar) =>
    post<ImportacionDTO>(`/importar/${que}`, { archivo: a?.base64 ?? '', simular, ...(porAlmacen ? { almacen_id: almacenId } : { actualizar_existentes: act }) });
  const revisar = useMutation({
    mutationFn: (v: { a: Archivo; act: boolean }) => enviar(true, v.a, v.act),
    onSuccess: setRevision,
    onError: (e) => { setRevision(null); toast.error('No se pudo leer el Excel', { description: mensajeDeError(e) }); },
  });
  const cargar = useMutation({
    mutationFn: () => enviar(false),
    onSuccess: (r) => {
      alCargar();
      // Existencia inicial: el título no dice «entraron»; la nota (aplicado o por aprobar) va debajo
      const hecho = porAlmacen ? (r.crear > 0 ? `Carga lista: ${r.crear} ${t.cosas} en el ajuste` : 'Carga lista: no entró nada') : `Carga lista: ${r.crear} ${t.cosas} ${t.crean[1]}${r.actualizar ? `, ${r.actualizar} actualizados` : ''}`;
      if (r.errores > 0) {
        // Quedaron filas fuera: el diálogo sigue abierto con el resultado, para que se vea cuáles y por qué
        setRevision(r);
        toast.warning(hecho, { description: `${r.errores} fila(s) no entraron: revisa el detalle.` });
        return;
      }
      toast.success(hecho, { description: r.nota ?? 'Queda en auditoría.' });
      reiniciar();
      cerrar();
    },
    onError: (e) => toast.error('No se pudo cargar', { description: mensajeDeError(e) }),
  });
  const reiniciar = () => { setArchivo(null); setRevision(null); setActualizar(false); if (entrada.current) entrada.current.value = ''; };
  const elegir = async (f: File | undefined) => {
    if (!f) return;
    if (f.size > MAX_BYTES) { toast.error('El archivo pesa más de 2 MB', { description: 'Divídelo en varios archivos.' }); return; }
    try {
      const a = { nombre: f.name, base64: await aBase64(f) };
      setArchivo(a);
      revisar.mutate({ a, act: actualizar });
    } catch (e) {
      toast.error(mensajeDeError(e));
    }
  };
  const cambiarActualizar = (v: boolean) => { setActualizar(v); if (archivo) revisar.mutate({ a: archivo, act: v }); };
  const cambiarAlmacen = (id: string) => { setAlmacenId(id); reiniciar(); };
  const nada = revision ? revision.aplicado || revision.crear + revision.actualizar === 0 : true;
  const hecha = revision?.aplicado === true;
  const i = hecha ? 1 : 0;
  const faltaAlmacen = porAlmacen && !almacenId;
  const plantilla = porAlmacen ? `/importar/existencias/plantilla?almacen_id=${almacenId}` : `/importar/${que}/plantilla`;
  return (
    <Dialog open={abierto} onOpenChange={(o) => { if (!o) { reiniciar(); cerrar(); } }}>
      <DialogContent className="max-w-3xl" data-testid="dialogo-importar">
        <DialogHeader>
          <div className="flex items-center gap-1"><DialogTitle>{t.titulo}</DialogTitle>{tema ? <AyudaVentana tema={tema} /> : null}</div>
          <DialogDescription>{t.descripcion} Una carga grande puede tardar un minuto: no cierres la ventana.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          {porAlmacen ? (
            <Select value={almacenId} onChange={(e) => cambiarAlmacen(e.target.value)} className="w-56" aria-label="Almacén" data-testid="importar-almacen">
              <option value="">Almacén…</option>
              {almacenes.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
            </Select>
          ) : null}
          <Button variant="outline" disabled={faltaAlmacen} onClick={() => descargar(plantilla, porAlmacen ? 'existencia_inicial.xlsx' : `plantilla_${que}.xlsx`).catch((e) => toast.error(mensajeDeError(e)))} data-testid="importar-plantilla"><Download /> Descargar plantilla</Button>
          <input ref={entrada} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={(e) => void elegir(e.target.files?.[0])} data-testid="importar-archivo" />
          <Button disabled={faltaAlmacen} onClick={() => entrada.current?.click()} cargando={revisar.isPending} data-testid="importar-elegir"><FileSpreadsheet /> {archivo ? 'Elegir otro archivo' : 'Elegir archivo'}</Button>
          {archivo ? <span className="text-xs text-muted-foreground">{archivo.nombre}</span> : null}
        </div>
        {porAlmacen ? null : (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={actualizar} onChange={(e) => cambiarActualizar(e.target.checked)} data-testid="importar-actualizar" />
            Si ya existe (mismo {que === 'productos' ? 'código' : 'RIF'}), actualizarlo con lo del archivo
          </label>
        )}
        {revision ? (
          <div className="space-y-2" data-testid="importar-revision">
            <p className="text-sm">
              {hecha ? <b data-testid="importar-hecha">Carga hecha. </b> : null}
              <b data-testid="importar-total">{revision.total}</b> filas: <b className="text-mg-verde" data-testid="importar-crear">{revision.crear}</b> {t.crean[i]}
              {porAlmacen ? null : <> · <b data-testid="importar-actualizan">{revision.actualizar}</b> {hecha ? 'actualizados' : 'se actualizan'}</>}
              {' '}· <b data-testid="importar-omiten">{revision.omitir}</b> {hecha ? 'quedaron igual' : 'se dejan igual'} · <b className={revision.errores ? 'text-mg-rojo' : ''} data-testid="importar-errores">{revision.errores}</b> {hecha ? 'no entraron' : 'con error'}
            </p>
            {revision.nota ? <p className="rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-900" data-testid="importar-nota">{revision.nota}</p> : null}
            <div className="max-h-72 overflow-auto rounded-lg border">
              <Table>
                <TableHeader><TableRow><TableHead>Fila</TableHead><TableHead>{t.clave}</TableHead><TableHead>Nombre</TableHead><TableHead>Qué pasa</TableHead><TableHead>Detalle</TableHead></TableRow></TableHeader>
                <TableBody>
                  {/* Primero los errores: son lo que hay que corregir en el Excel */}
                  {revision.filas.toSorted((a, b) => Number(b.accion === 'error') - Number(a.accion === 'error') || a.fila - b.fila).slice(0, 300).map((f) => (
                    <TableRow key={f.fila} data-testid={`importar-fila-${f.fila}`}>
                      <TableCell className="num">{f.fila}</TableCell>
                      <TableCell className="text-xs">{f.clave || '—'}</TableCell>
                      <TableCell className="max-w-56 truncate" title={f.nombre}>{f.nombre || '—'}</TableCell>
                      <TableCell><Badge variant={VARIANTE[f.accion]}>{(f.accion === 'crear' ? t.entra : ACCION[f.accion])[i]}</Badge></TableCell>
                      <TableCell className="text-xs">{f.mensaje ?? ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {revision.filas.length > 300 ? <p className="text-xs text-muted-foreground">Se muestran las primeras 300 filas (los errores primero).</p> : null}
            {revision.errores > 0 ? <p className="text-xs text-mg-rojo">{hecha ? 'Las filas marcadas no entraron; el resto sí.' : 'Las filas con error no se cargan.'} Puedes corregirlas en el Excel y subirlo de nuevo: las que ya entraron se dejan igual.</p> : null}
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={() => { reiniciar(); cerrar(); }} data-testid="importar-cerrar">{hecha ? 'Cerrar' : 'Cancelar'}</Button>
          <Button disabled={!revision || nada || revisar.isPending} cargando={cargar.isPending} onClick={() => cargar.mutate()} data-testid="importar-cargar">{revision && !nada ? `Cargar ${revision.crear + revision.actualizar} ${t.cosas}` : 'Cargar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
